const React = require('react');

const { vec2, mat2d, mat3 } = require('gl-matrix');

const vs = `
attribute highp vec2 a_xy;
attribute highp vec2 a_qr;

uniform mat3 u_transform;

varying highp vec2 v_qr;

void main(void) {
  gl_Position = vec4(a_xy, 0.0, 1.0);
  v_qr = (u_transform * vec3(a_qr, 1.0)).xy;
}
`;
const noiseSource = `
//
// Description : Array and textureless GLSL 2D/3D/4D simplex
//               noise functions.
//      Author : Ian McEwan, Ashima Arts.
//  Maintainer : stegu
//     Lastmod : 20110822 (ijm)
//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.
//               Distributed under the MIT License. See LICENSE file.
//               https://github.com/ashima/webgl-noise
//               https://github.com/stegu/webgl-noise
//

vec4 mod289(vec4 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0; }

float mod289(float x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0; }

vec4 permute(vec4 x) {
     return mod289(((x*34.0)+1.0)*x);
}

float permute(float x) {
     return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r)
{
  return 1.79284291400159 - 0.85373472095314 * r;
}

float taylorInvSqrt(float r)
{
  return 1.79284291400159 - 0.85373472095314 * r;
}

vec4 grad4(float j, vec4 ip)
  {
  const vec4 ones = vec4(1.0, 1.0, 1.0, -1.0);
  vec4 p,s;

  p.xyz = floor( fract (vec3(j) * ip.xyz) * 7.0) * ip.z - 1.0;
  p.w = 1.5 - dot(abs(p.xyz), ones.xyz);
  s = vec4(lessThan(p, vec4(0.0)));
  p.xyz = p.xyz + (s.xyz*2.0 - 1.0) * s.www;

  return p;
  }

// (sqrt(5) - 1)/4 = F4, used once below
#define F4 0.309016994374947451

vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec2 mod289(vec2 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec3 permute(vec3 x) {
  return mod289(((x*34.0)+1.0)*x);
}


float snoise2(vec2 v)
  {
  const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
                      0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
                     -0.577350269189626,  // -1.0 + 2.0 * C.x
                      0.024390243902439); // 1.0 / 41.0
// First corner
  vec2 i  = floor(v + dot(v, C.yy) );
  vec2 x0 = v -   i + dot(i, C.xx);

// Other corners
  vec2 i1;
  //i1.x = step( x0.y, x0.x ); // x0.x > x0.y ? 1.0 : 0.0
  //i1.y = 1.0 - i1.x;
  i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  // x0 = x0 - 0.0 + 0.0 * C.xx ;
  // x1 = x0 - i1 + 1.0 * C.xx ;
  // x2 = x0 - 1.0 + 2.0 * C.xx ;
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;

// Permutations
  i = mod289(i); // Avoid truncation effects in permutation
  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
		+ i.x + vec3(0.0, i1.x, 1.0 ));

  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m ;
  m = m*m ;

// Gradients: 41 points uniformly over a line, mapped onto a diamond.
// The ring size 17*17 = 289 is close to a multiple of 41 (41*7 = 287)

  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;

// Normalise gradients implicitly by scaling m
// Approximation of: m *= inversesqrt( a0*a0 + h*h );
  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );

// Compute final noise value at P
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

float snoise(vec4 v)
  {
  const vec4  C = vec4( 0.138196601125011,  // (5 - sqrt(5))/20  G4
                        0.276393202250021,  // 2 * G4
                        0.414589803375032,  // 3 * G4
                       -0.447213595499958); // -1 + 4 * G4

// First corner
  vec4 i  = floor(v + dot(v, vec4(F4)) );
  vec4 x0 = v -   i + dot(i, C.xxxx);

// Other corners

// Rank sorting originally contributed by Bill Licea-Kane, AMD (formerly ATI)
  vec4 i0;
  vec3 isX = step( x0.yzw, x0.xxx );
  vec3 isYZ = step( x0.zww, x0.yyz );
//  i0.x = dot( isX, vec3( 1.0 ) );
  i0.x = isX.x + isX.y + isX.z;
  i0.yzw = 1.0 - isX;
//  i0.y += dot( isYZ.xy, vec2( 1.0 ) );
  i0.y += isYZ.x + isYZ.y;
  i0.zw += 1.0 - isYZ.xy;
  i0.z += isYZ.z;
  i0.w += 1.0 - isYZ.z;

  // i0 now contains the unique values 0,1,2,3 in each channel
  vec4 i3 = clamp( i0, 0.0, 1.0 );
  vec4 i2 = clamp( i0-1.0, 0.0, 1.0 );
  vec4 i1 = clamp( i0-2.0, 0.0, 1.0 );

  //  x0 = x0 - 0.0 + 0.0 * C.xxxx
  //  x1 = x0 - i1  + 1.0 * C.xxxx
  //  x2 = x0 - i2  + 2.0 * C.xxxx
  //  x3 = x0 - i3  + 3.0 * C.xxxx
  //  x4 = x0 - 1.0 + 4.0 * C.xxxx
  vec4 x1 = x0 - i1 + C.xxxx;
  vec4 x2 = x0 - i2 + C.yyyy;
  vec4 x3 = x0 - i3 + C.zzzz;
  vec4 x4 = x0 + C.wwww;

// Permutations
  i = mod289(i);
  float j0 = permute( permute( permute( permute(i.w) + i.z) + i.y) + i.x);
  vec4 j1 = permute( permute( permute( permute (
             i.w + vec4(i1.w, i2.w, i3.w, 1.0 ))
           + i.z + vec4(i1.z, i2.z, i3.z, 1.0 ))
           + i.y + vec4(i1.y, i2.y, i3.y, 1.0 ))
           + i.x + vec4(i1.x, i2.x, i3.x, 1.0 ));

// Gradients: 7x7x6 points over a cube, mapped onto a 4-cross polytope
// 7*7*6 = 294, which is close to the ring size 17*17 = 289.
  vec4 ip = vec4(1.0/294.0, 1.0/49.0, 1.0/7.0, 0.0) ;

  vec4 p0 = grad4(j0,   ip);
  vec4 p1 = grad4(j1.x, ip);
  vec4 p2 = grad4(j1.y, ip);
  vec4 p3 = grad4(j1.z, ip);
  vec4 p4 = grad4(j1.w, ip);

// Normalise gradients
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;
  p4 *= taylorInvSqrt(dot(p4,p4));

// Mix contributions from the five corners
  vec3 m0 = max(0.6 - vec3(dot(x0,x0), dot(x1,x1), dot(x2,x2)), 0.0);
  vec2 m1 = max(0.6 - vec2(dot(x3,x3), dot(x4,x4)            ), 0.0);
  m0 = m0 * m0;
  m1 = m1 * m1;
  return 49.0 * ( dot(m0*m0, vec3( dot( p0, x0 ), dot( p1, x1 ), dot( p2, x2 )))
               + dot(m1*m1, vec2( dot( p3, x3 ), dot( p4, x4 ) ) ) ) ;

  }
`;

const gridSource = `
precision highp float;

varying highp vec2 v_qr;

uniform sampler2D u_map;
uniform int u_N;
uniform float u_EDGE;
uniform vec4 u_EDGE_COLOR;

void main(void) {

  float N = float(u_N);
  vec2 uv = vec2(floor(v_qr.x) / N, floor(v_qr.y) / N);

  float q = fract(v_qr.x);
  float r = fract(v_qr.y);

  if (q - r > 0.0) {
    float a = 1.0 - q;
    float b = q - r;
    float c = 1.0 - a - b;

    gl_FragColor = f(a, b, uv, uv + vec2(1.0/N, 0.0), uv + vec2(1.0/N, 1.0/N));

  }
  else {
    float a = 1.0 - r;
    float d = r - q;
    float c = 1.0 - a - d;
    gl_FragColor = f(a, c, uv, uv + vec2(1.0/N, 1.0/N), uv + vec2(0.0, 1.0/N));
  }
}

`
const fs = `
precision highp float;

varying highp vec2 v_qr;

uniform sampler2D u_map;
uniform int u_N;
uniform float u_EDGE;
uniform vec4 u_EDGE_COLOR;

${noiseSource}

float fbm(vec2 p, int octaves, float lacunarity, float gain) {
  float sum = 0.0;
  float amp = 1.0;
  vec2 pp = p;

  for(int i = 0; i < 1; i+=1)
  {
    amp *= gain;
    sum += amp * snoise2(pp);
    pp *= lacunarity;
  }
  return sum;

}

vec4 f(float a, float b, vec2 uvA, vec2 uvB, vec2 uvC) {

  float EDGE = u_EDGE;
  vec4 EDGE_COLOR = u_EDGE_COLOR;

  vec2 uv;
  float c = 1.0 - a - b;
  float ab = abs(a-b);
  float ac = abs(a-c);
  float bc = abs(b-c);
  vec4 color = vec4(1.0, 1.0, 1.0, 1.0);
  if (a > b && a > c) {
    if (ab < EDGE || ac < EDGE) color = EDGE_COLOR;
  }
  else if (b > c) {
    if (ab < EDGE || bc < EDGE) color = EDGE_COLOR;
  }
  else if (ac < EDGE || bc < EDGE) color = EDGE_COLOR;

  // a += (snoise2(uvA * float(u_N) + vec2(a,b)) + 0.0) / 5.0;
  c = 1.0 - a - b;
  if (a > b && a > c) {
    uv = uvA;
  }
  else if (b > c) {
    uv = uvB;
  }
  else {
    uv = uvC;
  }

  return color * texture2D(u_map, uv);
}

void main(void) {

  float N = float(u_N);
  vec2 uv = vec2(floor(v_qr.x) / N, floor(v_qr.y) / N);

  float q = fract(v_qr.x);
  float r = fract(v_qr.y);

  if (q - r > 0.0) {
    float a = 1.0 - q;
    float b = q - r;
    float c = 1.0 - a - b;

    gl_FragColor = f(a, b, uv, uv + vec2(1.0/N, 0.0), uv + vec2(1.0/N, 1.0/N));

  }
  else {
    float a = 1.0 - r;
    float d = r - q;
    float c = 1.0 - a - d;
    gl_FragColor = f(a, c, uv, uv + vec2(1.0/N, 1.0/N), uv + vec2(0.0, 1.0/N));
  }
}
`;

function compileShader(gl, type, source) {

  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader));
  }
  return shader;
}

const K = 1/Math.sqrt(3);

const qr_transform = (w, h) =>
  [ h * K/2, -h/2, h * K/2, h/2, w/2, h/2 ];

export class Grid extends React.Component {

  init(w, h) {
    const canvas = this.canvas;
    const gl = canvas.getContext("webgl");

    if (!gl) return;

    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width  = w * devicePixelRatio;
    canvas.height = h * devicePixelRatio;
    canvas.style.width  = "" + w + "px";
    canvas.style.height = "" + h + "px";
    gl.viewport(0, 0, canvas.width, canvas.height);

    const fullscreenMesh = [
    // x, y, q, r
      -1, 1, 0, 0,
       1, 1, 1, 0,
       1,-1, 1, 1,
    // x, y, q, r
      -1, 1, 0, 0,
       1,-1, 1, 1,
      -1,-1, 0, 1,
    ];
    var vertexAttributes = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexAttributes);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(fullscreenMesh), gl.STATIC_DRAW);

    var fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fs);
    var vertexShader   = compileShader(gl, gl.VERTEX_SHADER, vs);

    var shaderProgram = gl.createProgram();
    gl.attachShader(shaderProgram, vertexShader);
    gl.attachShader(shaderProgram, fragmentShader);
    gl.linkProgram(shaderProgram);

    if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(shaderProgram));
    }

    gl.useProgram(shaderProgram);

    var xy = gl.getAttribLocation(shaderProgram, "a_xy");
    gl.enableVertexAttribArray(xy);

    var qr = gl.getAttribLocation(shaderProgram, "a_qr");
    gl.enableVertexAttribArray(qr);

    if (this.texture) {
      gl.deleteTexture(this.texture);
      delete this.texture;
      delete this.map;
    }
    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(gl.getUniformLocation(shaderProgram, "u_map"), 0)

    gl.bindBuffer(gl.ARRAY_BUFFER, vertexAttributes);
    gl.vertexAttribPointer(xy,  2, gl.FLOAT, false, 4 * 4, 0);
    gl.vertexAttribPointer(qr,  2, gl.FLOAT, false, 4 * 4, 2 * 4);

    gl.clearColor(0.5, 0.5, 0.5, 1.0);

    this.shaderProgram = shaderProgram;
  }

  update(width, height, {transform, map, gridColorStrategy, gridSizeStrategy}) {
    const gl = this.canvas.getContext("webgl");
    if (!gl) return;

    const m = mat2d.create();
    mat2d.invert(m, transform);
    const t = mat3.fromMat2d(mat3.create(), m);
    mat3.scale(t, t, [width, height, 1]);

    gl.uniformMatrix3fv(gl.getUniformLocation(this.shaderProgram, "u_transform"), false, t);

    const [x1, y1] = vec2.transformMat2d([,,], [0, 0], transform);
    const [x2, y2] = vec2.transformMat2d([,,], [1, 1], transform);
    const d = Math.sqrt( (x2-x1)*(x2-x1) + (y2-y1)*(y2-y1));

    gl.uniform4f(gl.getUniformLocation(this.shaderProgram, "u_EDGE_COLOR"), ... gridColorStrategy() );
    gl.uniform1f(gl.getUniformLocation(this.shaderProgram, "u_EDGE"), gridSizeStrategy());

    if (!map) return;
    if (map != this.map) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, map.width, map.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, map.data);
      gl.uniform1i(gl.getUniformLocation(this.shaderProgram, "u_N"), map.width);
      this.map = map;
    }

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  shouldComponentUpdate() {
    return false;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.width != this.props.width || nextProps.height != this.props.height) {
      this.init(nextProps.width, nextProps.height);
      this.update(nextProps.width, nextProps.height, nextProps.model);
    }
    else {
      this.update(this.props.width, this.props.height, nextProps.model);
    }
  }

  componentDidMount() {

    this.setupWebGLStateAndResources = () => {
      this.init(this.props.width, this.props.height);
      this.update(this.props.width, this.props.height, this.props.model);
    };

    this.canvas.addEventListener("webglcontextlost", event => {
        event.preventDefault();
    }, false);

    this.canvas.addEventListener("webglcontextrestored", this.setupWebGLStateAndResources, false);

    this.setupWebGLStateAndResources();

  }

  render() {
    return <canvas ref={canvas => this.canvas = canvas } width={this.props.width} height={this.props.height} style={this.props.style}/>;
  }

}



/** WEBPACK FOOTER **
 ** ./src/grid.js
 **/